import { type AppNotification, describeNotification, type NotificationList } from "@cva/shared";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AtSign,
  Bell,
  CheckCheck,
  CircleAlert,
  CircleCheck,
  Download,
  Loader2,
  Settings,
  Users,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { client, orpc } from "../lib/api";
import { rememberOrg } from "../lib/session";
import { cn, errorMessage, formatDate, formatRelative } from "../lib/utils";
import { Avatar } from "./app-shell";

/**
 * The bell with the user's notifications (docs/15-zusammenarbeit.md): unread
 * badge, a panel grouped by day, mark as read, jump to the file and comment.
 * Polled (no WebSockets behind the proxy): the badge every 45 s and on focus.
 */

const POLL_MS = 45_000;
const PAGE_SIZE = 30;

export function useUnreadNotifications() {
  return useQuery({
    ...orpc.notifications.unread.queryOptions(),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

function dayLabel(date: Date): string {
  const today = new Date();
  const start = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round((start(today) - start(date)) / 86_400_000);
  if (days === 0) return "Heute";
  if (days === 1) return "Gestern";
  return formatDate(date);
}

function groupByDay(items: AppNotification[]) {
  const groups: { label: string; items: AppNotification[] }[] = [];
  for (const item of items) {
    const label = dayLabel(item.createdAt);
    const last = groups.at(-1);
    if (last?.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

function NotificationIcon({ notification }: { notification: AppNotification }) {
  const icon = (node: ReactNode, tone: string) => (
    <span
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-full [&_svg]:size-4",
        tone,
      )}
    >
      {node}
    </span>
  );
  if (notification.type === "export_done")
    return icon(<Download />, "bg-emerald-50 text-emerald-700");
  if (notification.type === "export_failed") return icon(<CircleAlert />, "bg-red-50 text-red-700");
  if (notification.type === "member_added" || notification.type === "role_changed") {
    return icon(<Users />, "bg-brand-50 text-brand-700");
  }
  const avatar = (
    <Avatar
      name={notification.actor.name ?? "?"}
      className={cn("size-8 text-xs", notification.actor.guest && "bg-zinc-100 text-zinc-600")}
    />
  );
  const badge =
    notification.type === "mention" ? (
      <AtSign />
    ) : notification.type === "resolved" ? (
      <CircleCheck />
    ) : null;
  return (
    <span className="relative inline-flex size-8 shrink-0">
      {avatar}
      {badge ? (
        <span className="absolute -right-1 -bottom-1 inline-flex size-4 items-center justify-center rounded-full bg-brand-600 text-white ring-2 ring-white [&_svg]:size-2.5">
          {badge}
        </span>
      ) : null}
    </span>
  );
}

function NotificationItem({
  notification,
  orgId,
  onOpen,
}: {
  notification: AppNotification;
  orgId: string;
  onOpen: (notification: AppNotification) => void;
}) {
  const { actor, text } = describeNotification({
    type: notification.type,
    actorName: notification.actor.name,
    documentTitle: notification.document?.title ?? null,
    organizationName: notification.organization.name,
    details: notification.details,
  });
  const unread = !notification.readAt;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className={cn(
          "flex w-full gap-3 px-4 py-3 text-left transition hover:bg-zinc-50",
          unread && "bg-brand-50/40",
        )}
      >
        <NotificationIcon notification={notification} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-zinc-800">
            {actor ? <span className="font-semibold text-zinc-900">{actor}</span> : null}
            {notification.actor.guest ? (
              <span className="ml-1 rounded bg-zinc-100 px-1 text-[10px] text-zinc-500">Gast</span>
            ) : null}
            {actor ? " " : ""}
            {text}
          </span>
          {notification.snippet ? (
            <span className="mt-0.5 line-clamp-2 block text-xs text-zinc-500">
              {notification.snippet}
            </span>
          ) : null}
          <span className="mt-1 block text-[11px] text-zinc-400">
            {formatRelative(notification.createdAt)}
            {notification.organization.id !== orgId ? ` · ${notification.organization.name}` : ""}
          </span>
        </span>
        {unread ? (
          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-600" title="Ungelesen" />
        ) : null}
      </button>
    </li>
  );
}

function NotificationPanel({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listKey = orpc.notifications.list.key();
  const list = useInfiniteQuery(
    orpc.notifications.list.infiniteOptions({
      input: (before: Date | undefined) => ({ limit: PAGE_SIZE, ...(before ? { before } : {}) }),
      initialPageParam: undefined,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      refetchInterval: POLL_MS,
      staleTime: 0,
    }),
  );
  const markRead = useMutation({
    mutationFn: (input: { ids: string[] } | { all: true }) => client.notifications.markRead(input),
    onMutate: (input) => {
      // Optimistic: the dot and the badge go away right away.
      const now = new Date();
      queryClient.setQueriesData<InfiniteData<NotificationList>>({ queryKey: listKey }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map((item) =>
                  !item.readAt && ("all" in input || input.ids.includes(item.id))
                    ? { ...item, readAt: now }
                    : item,
                ),
              })),
            }
          : data,
      );
    },
    onSuccess: ({ unread }) => {
      queryClient.setQueryData(orpc.notifications.unread.queryKey(), { count: unread });
    },
    onError: (error) => toast.error(errorMessage(error)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = useUnreadNotifications().data?.count ?? 0;

  const open = (notification: AppNotification) => {
    if (!notification.readAt) markRead.mutate({ ids: [notification.id] });
    onClose();
    const target = notification.organization.id;
    if (target !== orgId) rememberOrg(target);
    if (notification.document) {
      void navigate({
        to: "/o/$orgId/documents/$documentId",
        params: { orgId: target, documentId: notification.document.id },
        search: notification.threadId ? { comment: notification.threadId } : {},
      });
    } else {
      void navigate({ to: "/o/$orgId", params: { orgId: target } });
    }
  };

  return (
    <div className="flex max-h-[min(36rem,80vh)] flex-col">
      <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
        <h2 className="flex-1 text-sm font-semibold text-zinc-900">Benachrichtigungen</h2>
        <button
          type="button"
          disabled={unread === 0 || markRead.isPending}
          onClick={() => markRead.mutate({ all: true })}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:text-zinc-400 disabled:hover:bg-transparent [&_svg]:size-3.5"
        >
          <CheckCheck /> Alle als gelesen markieren
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isLoading ? (
          <div className="flex justify-center py-10 text-zinc-400">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-zinc-500">
            <Bell className="mx-auto mb-2 size-6 text-zinc-300" />
            Noch keine Benachrichtigungen. Hier erscheinen Kommentare, Antworten und Erwähnungen,
            die dich betreffen, und fertige Exporte.
          </div>
        ) : (
          groupByDay(items).map((group) => (
            <section key={group.label}>
              <h3 className="sticky top-0 z-10 bg-white/95 px-4 pt-3 pb-1 text-[11px] font-semibold text-zinc-500 backdrop-blur">
                {group.label}
              </h3>
              <ul>
                {group.items.map((item) => (
                  <NotificationItem key={item.id} notification={item} orgId={orgId} onOpen={open} />
                ))}
              </ul>
            </section>
          ))
        )}
        {list.hasNextPage ? (
          <div className="p-3 text-center">
            <button
              type="button"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
              className="text-xs font-medium text-brand-700 hover:underline disabled:text-zinc-400"
            >
              {list.isFetchingNextPage ? "Lädt …" : "Ältere anzeigen"}
            </button>
          </div>
        ) : null}
      </div>
      <div className="border-t border-zinc-100 px-4 py-2.5">
        <Link
          to="/account"
          hash="benachrichtigungen"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 [&_svg]:size-3.5"
        >
          <Settings /> E-Mail-Benachrichtigungen einstellen
        </Link>
      </div>
    </div>
  );
}

/** Bell with badge; opens the notification panel. */
export function NotificationBell({
  orgId,
  side = "bottom",
  align = "start",
  className,
}: {
  orgId: string;
  side?: "bottom" | "right" | "left" | "top";
  align?: "start" | "center" | "end";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const unread = useUnreadNotifications();
  const count = unread.data?.count ?? 0;
  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          void queryClient.invalidateQueries({ queryKey: orpc.notifications.unread.queryKey() });
        }
      }}
    >
      <PopoverPrimitive.Trigger
        title={count > 0 ? `Benachrichtigungen (${count} ungelesen)` : "Benachrichtigungen"}
        aria-label={count > 0 ? `Benachrichtigungen, ${count} ungelesen` : "Benachrichtigungen"}
        className={cn(
          "relative inline-flex size-9 items-center justify-center rounded-lg text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 data-[state=open]:bg-zinc-100 [&_svg]:size-[18px]",
          className,
        )}
      >
        <Bell />
        {count > 0 ? (
          <span className="absolute top-1 right-1 min-w-4 rounded-full bg-red-600 px-1 text-[10px] leading-4 font-semibold text-white ring-2 ring-white">
            {count > 9 ? "9+" : count}
          </span>
        ) : null}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[min(24rem,calc(100vw-24px))] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl outline-none"
        >
          <NotificationPanel orgId={orgId} onClose={() => setOpen(false)} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
